import { useEffect } from 'react';
import clsx from 'clsx';
import { X } from '@phosphor-icons/react';
import { useToastStore, type Toast } from '@/state/toastStore';

const AUTO_DISMISS_MS = 4_000;

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <div
      role="region"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-0 bottom-4 z-50 flex flex-col items-center gap-2 px-4"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const handle = setTimeout(onDismiss, AUTO_DISMISS_MS);
    return () => clearTimeout(handle);
  }, [onDismiss]);

  return (
    <div
      role="status"
      className="pointer-events-auto flex max-w-md items-start gap-3 rounded-md bg-surface-3 p-3 pl-4 text-[13px] text-text-1 shadow-elev-2"
      style={{ borderLeft: `3px solid ${borderColor(toast.kind)}` }}
    >
      <p className="flex-1">{toast.message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className={clsx(
          'rounded-sm p-1 text-text-3 transition-colors hover:bg-surface-2 hover:text-text-1',
        )}
      >
        <X size={12} weight="bold" />
      </button>
    </div>
  );
}

function borderColor(kind: Toast['kind']): string {
  switch (kind) {
    case 'ok':
      return 'var(--color-ok)';
    case 'warn':
      return 'var(--color-warn)';
    case 'err':
      return 'var(--color-err)';
    default:
      return 'var(--color-info)';
  }
}
